import type { MeetingDetail, MeetingKind } from './calendar.ts';
import type { MeetingTemplateColor } from './meetingTemplate.ts';
import type { JourneyMeetingLabel } from './journey.ts';

export type { MeetingKind };

/**
 * Which level settled a switch. The page shows one line per switch — because of
 * your choice for this kind of meeting, because of a choice for this thread,
 * because of a choice for this one time — and this is what picks it. `floor` is
 * the rule no choice lifts: a cancelled or declined meeting, an event nobody
 * attends, an event with a crowd, an event that runs all day.
 */
export type MeetingFollowLevel = 'floor' | 'occurrence' | 'thread' | 'committed' | 'kind';

/** What settled the decision, in one word. */
export type MeetingFollowReason =
  | 'cancelled'
  | 'declined'
  /* The organisation's agendaproxy blocked it: Wundo knows only its times. */
  | 'blocked'
  | 'no_participants'
  | 'too_many_participants'
  | 'too_long'
  | 'occurrence_override'
  | 'thread_override'
  | 'no_preferences'
  | 'follow_all'
  | 'kind_followed'
  | 'kind_not_followed'
  /* Wundo is already on this meeting, so a filter change no longer reaches it. */
  | 'committed'
  /* The user wrote this meeting down in Wundo. That is the whole answer. */
  | 'manual_meeting';

/** Whether Wundo helps and whether the notetaker joins, plus why. */
export interface MeetingPageDecision {
  follow: boolean;
  record: boolean;
  kind: MeetingKind;
  reason: MeetingFollowReason;
  /** Which level settled whether Wundo helps. */
  level: MeetingFollowLevel;
  /** Why the notetaker stands as it does — the switches are settled apart. */
  record_reason: MeetingFollowReason;
  /** Which level settled whether the notetaker joins. */
  record_level: MeetingFollowLevel;
  /**
   * Whether this is an event Wundo never picks up on its own, whatever the
   * user chose: one nobody attends, one with a crowd, or one that runs all day.
   * Only a correction for this one occurrence reaches it, so the page must not
   * offer a standing one.
   */
  never_automatic: boolean;
}

/** What a thread is called, for the thread line and the pickers. */
export interface MeetingThreadItem {
  id: string;
  name: string;
  kind: MeetingKind;
}

/**
 * The thread as its own meeting page holds it: what it is called, plus the
 * user's scratchpad for it.
 *
 * A separate type from `MeetingThreadItem` on purpose. That one is also the
 * picker's row, and a list of every thread the user has is the last place their
 * private notes belong — one page, one thread, is the only shape that needs
 * them. Empty is `''`: a scratchpad never written in and one emptied again are
 * the same empty box.
 */
export interface MeetingPageThread extends MeetingThreadItem {
  private_notes: string;
  /**
   * How many meetings in this thread happened before this one. What lets the
   * line under the title say something the title has not already said —
   * "terugkerende meeting, 4 eerdere afspraken" rather than the thread's name
   * repeated back. Earlier than this meeting, and never counting it.
   */
  past_meeting_count: number;
  /**
   * How many meetings of this conversation are on the calendar at all — past
   * and future, whether or not Wundo took part in them, this one included.
   *
   * What decides whether a choice is worth asking a scope question about: at
   * one, "this meeting" and "all conversations" are the same answer, and asking
   * would be a question with one meaning and two buttons.
   */
  meeting_count: number;
}

/**
 * The preparation: one markdown document. Generation writes the template's
 * section titles as headings with Wundo's suggestions as checklist lines under
 * them; from that moment the document is the user's — headings included.
 * Regenerating replaces the whole document after a confirm.
 */
export interface MeetingPrepContent {
  text: string;
}

/**
 * The stored preparation. `generated_at` is null while it holds only the user's
 * own lines — a preparation can exist before Wundo has written anything.
 */
export interface MeetingPagePrep {
  content: MeetingPrepContent;
  generated_at: string | null;
}

/**
 * The previous conversation in this thread, in one paragraph. A live read of
 * that meeting's verslag, never a copy: correcting it there corrects it here.
 */
export interface MeetingPageRecap {
  /** The meeting this recap was read from, so a reader can open its verslag. */
  meeting_id: string;
  meeting_starts_at: string;
  text: string;
  /**
   * The conversation this summary has already been asked about, or null while
   * it has only been shown.
   *
   * About *this* page's meeting, not the one above: the chat drawer opens the
   * summary as a card until somebody says something about it, and this is what
   * says that has happened. Without it every visit would put the same card in
   * front of a user who is already talking about it, and the next thing they
   * said would start a second conversation about one meeting.
   */
  conversation_id: string | null;
}

/** An open agreement on the thread, as the preparation lists it. */
export interface MeetingActionItem {
  id: string;
  text: string;
  /** Which meeting it came out of, when that meeting still exists. */
  meeting_id: string | null;
  created_at: string;
}

/**
 * An agreement that is done. The same row as an open one, plus the moment it
 * was ticked off — which is the only thing the archive has to say about it.
 */
export interface MeetingCompletedActionItem extends MeetingActionItem {
  done_at: string;
}

/** Everything ever ticked off on one thread, newest first. */
export interface MeetingCompletedActionItemsResponse {
  data: MeetingCompletedActionItem[];
}

/**
 * One section of the verslag: the template's title, and whatever the meeting's
 * material said about it, as markdown. An empty text is a valid answer — the
 * material said nothing about that theme, and Wundo does not fill the gap.
 */
export interface MeetingReportSection {
  title: string;
  text: string;
}

/**
 * The verslag: a plain document with no states. `generated_at` is when this
 * meeting first had one — editing it does not make it newer.
 */
export interface MeetingPageReport {
  sections: MeetingReportSection[];
  generated_at: string;
}

/**
 * A piece of material the verslag is written from.
 *
 * A union rather than one shape with optional fields: the transcript may only
 * ever say that it exists. Giving it no `id` and no text is what makes "the
 * transcript is never shown" something the type enforces instead of something
 * a controller has to remember.
 */
export interface MeetingTranscriptMaterial {
  kind: 'transcript';
  /** When the notetaker was booked for this meeting. */
  created_at: string;
}

/**
 * The longest note the page accepts in one go — a pasted transcript still
 * fits. Forms check it before sending, so the API and the field never drift.
 */
export const MATERIAL_MAX_LENGTH = 50_000;

export interface MeetingPastedMaterial {
  id: string;
  kind: 'pasted';
  created_at: string;
  /**
   * The opening of the note, line breaks and all, so the user can read back
   * what they wrote rather than only recognise it. Cut off with an ellipsis
   * when the note goes on — a 50k paste never ships whole.
   */
  excerpt: string;
}

export type MeetingPageMaterial = MeetingTranscriptMaterial | MeetingPastedMaterial;

/**
 * The verslag as one piece of plain text: every section that has something to
 * say, its title, then its text.
 *
 * One implementation for three readers — the server, which stores it beside the
 * sections so the chat and the recap keep reading what they always read, the
 * copy button, and the tests. A second copy could only drift from this one.
 */
export function renderMeetingReport(sections: MeetingReportSection[]): string {
  return sections
    .filter((section) => section.text.trim().length > 0)
    .map((section) => `${section.title}\n\n${section.text.trim()}`)
    .join('\n\n');
}

/** The gesprekstype this meeting's preparation and verslag follow. */
export interface MeetingPageTemplate {
  id: string;
  name: string;
  color: MeetingTemplateColor;
}

/**
 * Where a document stands. `reading` is the moment between the file arriving
 * and Wundo having read it; `ready` means the summary is the document's own;
 * `failed` means the read did not work — the file is still there, and the page
 * says so rather than hiding it.
 */
export type MeetingPageDocumentStatus = 'reading' | 'ready' | 'failed';

/**
 * A document on the conversation this meeting belongs to: read once when it
 * arrived, then carried by every meeting in the thread.
 *
 * Eight fields, named one by one. The extracted text is not among them — it is
 * what the model reads through `open`, and a page that lists documents has no
 * use for it. Neither is the storage key: `url` is the one way the bytes come
 * back out, through the backend.
 */
export interface MeetingPageDocument {
  id: string;
  name: string;
  mime: string;
  size: number;
  status: MeetingPageDocumentStatus;
  /** Two or three sentences about the document, or null until it is read. */
  summary: string | null;
  created_at: string;
  /** Where the file itself is downloaded from. */
  url: string;
}

/**
 * The intake: what Wundo opens the chat with when it has nothing to prepare
 * from and the preparation is still empty.
 *
 * The text is rendered by the server, in the user's own language, because the
 * same sentence has to become the first message of the conversation the user's
 * answer starts. The app shows it as an unsent bubble; nothing is created until
 * the user replies.
 */
export interface MeetingPrepIntake {
  opener: string;
  /**
   * Who the conversation is with, when the invitation names exactly one other
   * person; null when it names none or several.
   *
   * The same fact the opener is written from, handed over rather than worked
   * out again: the page's own empty state says "Ik ken Nina nog niet" beside an
   * opener that just said the same, and one rule for both is what keeps them
   * agreeing about who this conversation is with.
   */
  counterpart: string | null;
  /**
   * The conversation this question has already been answered in, or null while
   * it has been asked and nothing more.
   *
   * The page goes on offering the intake until the preparation exists, and
   * answering the opener is not what makes it exist — so without this a second
   * visit would put the same blank opener in front of someone who is halfway
   * through answering it, and a second answer would start a second conversation
   * about one meeting. With it, the drawer picks up where they left off.
   */
  conversation_id: string | null;
}

/**
 * The microphone recording of a physical meeting, as the page renders it: a
 * start button, a live recording, or the wait for its transcript.
 */
export interface MeetingPageOfflineRecording {
  /**
   * `idle` when nothing has been recorded here, `recording` while chunks are
   * arriving, `processing` between stop and transcript, `done` once the
   * transcript has landed, `failed` when transcription broke.
   *
   * `done` is a state of its own, and not a return to `idle`: recording again
   * replaces the finished one, so the page has to warn before it does.
   *
   * Always the reader's own recording. A colleague's never shows here.
   */
  status: 'idle' | 'recording' | 'processing' | 'done' | 'failed';
  /** When the running recording started, null unless status is `recording`. */
  started_at: string | null;
  /** The next chunk index to upload, so a reopened page continues the count. */
  next_chunk_index: number;
}

/**
 * The debrief: what Wundo opens the chat with when the meeting is over and it
 * has nothing to write the verslag from — no transcript, no pasted note, no
 * verslag of its own.
 *
 * The text is rendered by the server, in the user's own language, because the
 * same sentence has to become the first message of the conversation the user's
 * answer starts. The app shows it as an unsent bubble; nothing is created until
 * the user replies.
 */
export interface MeetingPageDebrief {
  opener: string;
  /**
   * The conversation this question is already being answered in, or null while
   * it has been asked and nothing more.
   *
   * The page goes on offering the debrief until a verslag exists, and answering
   * the opener is not what makes it exist — so without this a reload would put
   * the same opener in front of somebody halfway through answering it, and the
   * next thing they said would start a second conversation about one meeting.
   */
  conversation_id: string | null;
}

/** Everything the meeting page renders above its tabs. */
export interface MeetingPageResponse {
  meeting: MeetingDetail;
  /** The journey step this meeting is linked to, or null when it is linked to none. */
  journey: JourneyMeetingLabel | null;
  /** The thread this meeting belongs to, or null when it belongs to none. */
  thread: MeetingPageThread | null;
  decision: MeetingPageDecision;
  /** Microphone recording of a physical meeting — see the type. */
  offline_recording: MeetingPageOfflineRecording;
  /** The preparation, or null while there is none yet. */
  prep: MeetingPagePrep | null;
  /** This meeting's own verslag, or null while there is none yet. */
  report: MeetingPageReport | null;
  /**
   * What the verslag is written from. The transcript appears as its existence
   * and nothing more; pasted notes carry a readable opening of the note itself,
   * so the user can read back what they wrote before editing or deleting it.
   */
  materials: MeetingPageMaterial[];
  /** The previous conversation's verslag, or null when there is none. */
  recap: MeetingPageRecap | null;
  /**
   * The documents of this meeting's thread, oldest first. Empty when the
   * meeting belongs to no thread — a document hangs on the conversation, so
   * there is nowhere for one to live yet.
   */
  documents: MeetingPageDocument[];
  /** What is still open on the thread — a live view, not part of the document. */
  action_items: MeetingActionItem[];
  /**
   * How many of this thread's agreements are done. Only the count travels with
   * the page: it is what the "afgerond" line needs to know whether to show at
   * all, and the archive itself is fetched when that line is clicked.
   */
  action_items_done_count: number;
  /** The gesprekstype this preparation follows, or null when none resolved. */
  template: MeetingPageTemplate | null;
  /** Whether the user may pick another gesprekstype here. True on every meeting. */
  template_changeable: boolean;
  /**
   * Which gesprekstypes the picker offers: those whose `kind` equals this. Null
   * on a meeting the user wrote down themselves, which offers every kind.
   */
  template_kind: MeetingKind | null;
  /**
   * Whether the type is one the user picked themselves — for this meeting or
   * for the whole conversation — rather than the default it falls back to.
   */
  template_chosen: boolean;
  /**
   * Whether Wundo can write a preparation here at all: it follows the meeting,
   * the meeting is in a thread, a template resolved, the meeting is still to
   * come, and there is something to write the preparation *from*. The page
   * shows the invitation to generate — and the button to write it again — only
   * when this is true, so neither can be pressed into a refusal.
   */
  prep_available: boolean;
  /**
   * The opener for the intake, or null when there is nothing to ask about.
   *
   * Non-null only where a preparation could be written but there is nothing to
   * write it from, and nothing has been written yet. That is the one case the
   * page does not auto-generate: it shows the opener in the chat drawer and
   * lets the user answer instead.
   */
  prep_intake: MeetingPrepIntake | null;
  /**
   * The opener for the debrief, or null when there is nothing to ask about.
   *
   * Non-null only on a meeting that is over which Wundo has no material about
   * and no verslag for. That is the one case the verslag tab cannot be written
   * from anything: instead of asking for material, the chat asks the user how it
   * went and writes the verslag from the answers.
   */
  debrief: MeetingPageDebrief | null;
  /**
   * Whether there is anything to write a verslag from. Wundo never invents what
   * happened, so with no material the page asks for some instead of offering a
   * button that could only refuse.
   */
  report_available: boolean;
  /**
   * What a verslag for this reader is on its way from, without them asking for
   * it here; null when nothing is. The page waits on it rather than offering to
   * write one. See `MeetingReportPending`.
   */
  report_pending: MeetingReportPending | null;
  /**
   * Why the notetaker is not recording this meeting, or null when nothing is
   * wrong. See `MeetingPageNotetakerIssue`.
   */
  notetaker_issue: MeetingPageNotetakerIssue | null;
}

/**
 * - `waiting_room` and `waiting_for_permission` — live: the notetaker is at
 *   the meeting and waits to be let in, or to be allowed to record.
 * - the rest — afterwards: the notetaker left without a recording to write a
 *   verslag from, grouped by why. `failed` is everything we cannot explain.
 */
export type MeetingNotetakerIssueKind =
  | 'waiting_room'
  | 'waiting_for_permission'
  | 'not_admitted'
  | 'blocked'
  | 'removed'
  | 'recording_denied'
  | 'link_broken'
  | 'nobody_joined'
  | 'too_little_said'
  | 'failed';

/** What the meeting page tells the user about a notetaker that has a problem. */
export interface MeetingPageNotetakerIssue {
  kind: MeetingNotetakerIssueKind;
  /** Since when this is the case. */
  since: string;
  /** When the notetaker stops waiting and leaves; null when it does not say. */
  gives_up_at: string | null;
}

/**
 * - `microphone` — their own recording, stopped and not yet written up.
 * - `notetaker` — the meeting is over and the notetaker's recording of it is
 *   still being transcribed or written up.
 * - `debrief` — the chat saved what they told it and is writing from that.
 */
export type MeetingReportPending = 'microphone' | 'notetaker' | 'debrief';

/** The user's threads, for the move and merge pickers. */
export interface MeetingThreadsResponse {
  items: MeetingThreadItem[];
}

/** How far a correction made on the page reaches. */
export type MeetingCorrectionScope = 'occurrence' | 'always';

/**
 * How far a gesprekstype picked on the page reaches: this one sitting, or the
 * whole conversation. Its own type rather than the correction's — the two
 * happen to have the same two answers, and nothing says they always will.
 */
export type MeetingTemplateScope = 'occurrence' | 'always';

/** Which of a meeting's two documents the user read. */
export type MeetingOpenedDocument = 'prep' | 'report';

export interface MeetingOpenedRequest {
  document: MeetingOpenedDocument;
}

/**
 * A correction made on the meeting page. A key that is absent is left alone, so
 * flipping the notetaker never touches a standing choice about following; null
 * clears the correction and goes back to inheriting.
 */
export interface MeetingCorrectionRequest {
  follow?: boolean | null;
  record?: boolean | null;
  scope: MeetingCorrectionScope;
}

/**
 * One speaking turn of a meeting's transcript, as the superadmin-only
 * transcript view shows it. `start`/`end` are seconds into the recording,
 * null where the recorder gave no timings.
 */
export interface MeetingTranscriptSegment {
  speaker: string | null;
  text: string;
  start: number | null;
  end: number | null;
}

/**
 * A meeting's raw transcript. Superadmin-only: the endpoint answers 404 for
 * everyone else, as if it does not exist.
 */
export interface MeetingTranscriptResponse {
  segments: MeetingTranscriptSegment[];
}
